import { signalStoreFeature, type, withMethods } from '@ngrx/signals';
import { WidgetState } from './widget.state';
import { inject } from '@angular/core';
import { WidgetHelperService } from '../../services/widget/widget-helper.service';
import {
    WidgetConfig,
    WidgetType,
    WegweiserIndicator,
    WegweiserRegion,
} from '../../services/grid-widget-config.service';
import { ChartGroup, BaseDataset } from './chart.types';

/**
 * Feature providing Wegweiser-specific data transformation methods.
 * These methods convert external Wegweiser data into Chart.js compatible formats.
 */
export function withWegweiserMethods() {
    return signalStoreFeature(
        {
            state: type<WidgetState>(),
        },
        withMethods((store, helper = inject(WidgetHelperService)) => {
            // Access get and selected from the store (provided by withWidgetCoreFeatures)
            const { get, selected } = store as any;
            /**
             * Generates grouped Wegweiser data based on the provided widget configuration.
             *
             * This function takes a `widgetId` and determines the configuration for creating
             * data groups. The grouping is dynamically performed based on the `focusProperty`
             * and `groupProperty` defined in the widget configuration. It supports different
             * groupings such as by indicators, regions, and years, or combinations thereof.
             *
             * If no valid configuration is found or if necessary properties are missing,
             * it returns an empty object with default properties for labels, datasets, and groups.
             *
             * @param {string} widgetId - The identifier of the widget whose configuration is used
             *                            to determine grouping criteria.
             * @returns {Object} The grouped Wegweiser data, containing labels, datasets, and groups.
             */
            const getGroupedWegweiserData = (widgetId: string): { labels: string[]; datasets: BaseDataset[]; groups: ChartGroup[] } => {
                const config = widgetId ? get(widgetId) : { ...selected() };
                if (
                    !config ||
                    !config.type ||
                    !config.widget ||
                    !config.widget.wegweiserData ||
                    !config.widget.wegweiserData?.indicators.length
                ) {
                    return { labels: [], datasets: [], groups: [] };
                }

                const focusProperty = config.widget.focusProperty;
                const groupProperty = config.widget.groupProperty;

                switch (focusProperty) {
                    case 'dataLayer':
                        return (groupProperty === 'indicator'
                            ? getWegweiserDataLayerDataGroupedByIndicators(
                                Number(config.widget.year),
                                config.type,
                                config.widget
                            )
                            : getWegweiserDataLayerDataGroupedByRegions(
                                Number(config.widget.year),
                                config.type,
                                config.widget
                            )) || { labels: [], datasets: [], groups: [] };
                    case 'indicator':
                        return (groupProperty === 'feature'
                            ? getWegweiserIndicatorDataGroupedByRegions(
                                Number(config.widget.indicator),
                                config.type,
                                config.widget
                            )
                            : getWegweiserIndicatorDataGroupedByYears(
                                Number(config.widget.indicator),
                                config.type,
                                config.widget
                            )) || { labels: [], datasets: [], groups: [] };
                    case 'feature':
                        return (groupProperty === 'indicator'
                            ? getWegweiserRegionDataGroupedByIndicators(
                                Number(config.widget.feature),
                                config.type,
                                config.widget
                            )
                            : getWegweiserRegionDataGroupedByYears(
                                Number(config.widget.feature),
                                config.type,
                                config.widget
                            )) || { labels: [], datasets: [], groups: [] };
                }

                // Default fallback
                return { labels: [], datasets: [], groups: [] };
            };

            /**
             * Transforms WegweiserData into a Chart.js data format for a bar chart,
             * where indicators are on the x-axis, and regions are in the legend for a single year.
             *
             * @param year - The ID of the indicator to focus on.
             * @param type - The WidgetType
             * @param widget - The WidgetConfig
             * @returns A Chart.js compatible data object.
             */
            const getWegweiserDataLayerDataGroupedByIndicators = (
                year: number,
                type: WidgetType,
                widget: WidgetConfig
            ): { labels: string[]; datasets: BaseDataset[]; groups: ChartGroup[] } | null => {
                const data = widget.wegweiserData;
                if (!data) return null;

                let yearIndex = data.years.indexOf(year);
                if (yearIndex === -1) {
                    yearIndex = data.years.length - 1;
                }

                const groups: ChartGroup[] = [];
                // Extract indicator names (x-axis labels)
                const labels = data.indicators.map(indicator => {
                    groups.push({
                        label: indicator.name,
                        key: String(indicator.id),
                    });
                    return indicator.name;
                });

                // Create datasets for each region
                const datasets: BaseDataset[] = data.regions.map(
                    (region: WegweiserRegion, idx: number) => {
                        const color = helper.getChartBackgroundColor(
                            type,
                            widget.colorset,
                            widget.invertColors,
                            idx
                        );

                        return {
                            label: region.name,
                            labelKey: String(region.id),
                            data: data.indicators.map(indicator => {
                                // Extract the value for the region and given year index
                                const regionIndex = data.regions.findIndex(
                                    r => r.id === region.id
                                );
                                return (
                                    indicator.regionYearValues[regionIndex]?.[yearIndex] ?? null
                                );
                            }),
                            dataKeys: data.indicators.map(indicator =>
                                String(indicator.id)
                            ),
                            backgroundColor: color,
                            borderColor: color,
                            pointRadius: widget.dotSize || 4,
                            borderWidth: widget.borderWidth || 2,
                            tension: widget.lineTension || 0,
                        };
                    }
                );

                return { labels, datasets, groups };
            };

            /**
             * Transforms WegweiserData into a Chart.js data format for a bar chart,
             * where regions are on the x-axis, and indicators are in the legend for a single year.
             *
             * @param year - The ID of the indicator to focus on.
             * @param type - The WidgetType
             * @param widget - The WidgetConfig
             *
             * @returns A Chart.js compatible data object.
             */
            const getWegweiserDataLayerDataGroupedByRegions = (
                year: number,
                type: WidgetType,
                widget: WidgetConfig
            ): { labels: string[]; datasets: BaseDataset[]; groups: ChartGroup[] } | null => {
                // Find the index of the given year
                const data = widget.wegweiserData;
                if (!data) return null;

                let yearIndex = data.years.indexOf(year);
                if (yearIndex === -1) {
                    yearIndex = data.years.length - 1;
                }

                // X-Axis Labels (Regions)
                const groups: ChartGroup[] = [];
                const labels = data.regions.map(region => {
                    groups.push({
                        label: region.name,
                        key: String(region.id),
                    });
                    return region.name;
                });

                // Create datasets for each indicator
                const datasets: BaseDataset[] = data.indicators.map(
                    (indicator: WegweiserIndicator, idx: number) => {
                        const color = helper.getChartBackgroundColor(
                            type,
                            widget.colorset,
                            widget.invertColors,
                            idx
                        );

                        return {
                            label: indicator.name,
                            labelKey: String(indicator.id),
                            data: data.regions.map(region => {
                                // Extract the value for this region and the given year index
                                const regionIndex = data.regions.findIndex(
                                    r => r.id === region.id
                                );
                                return (
                                    indicator.regionYearValues[regionIndex]?.[yearIndex] ?? null
                                );
                            }),
                            dataKeys: data.regions.map(region => String(region.id)),
                            backgroundColor: color,
                            borderColor: color,
                            pointRadius: widget.dotSize || 4,
                            borderWidth: widget.borderWidth || 2,
                            tension: widget.lineTension || 0,
                        };
                    }
                );

                return { labels, datasets, groups };
            };

            /**
             * Transforms WegweiserData into a Chart.js data format for a bar chart,
             * where years  are on the x-axis, and indicators are in the legend for a single region.
             *
             * @param regionId - The ID of the indicator to focus on.
             * @param type - The WidgetType
             * @param widget - The WidgetConfig
             *
             * @returns A Chart.js compatible data object.
             */
            const getWegweiserRegionDataGroupedByYears = (
                regionId: number,
                type: WidgetType,
                widget: WidgetConfig
            ): { labels: string[]; datasets: BaseDataset[]; groups: ChartGroup[] } | null => {
                const data = widget.wegweiserData;
                if (!data) return null;

                // Find the index of the selected region
                const regionIndex = data.regions.findIndex(
                    region => region.id === regionId
                );
                if (regionIndex === -1) {
                    throw new Error(`Region with ID ${regionId} not found.`);
                }

                const groups: ChartGroup[] = [];
                const disabledYears = widget.disabledYears || [];
                const disabledYearsIdx: number[] = disabledYears.map(disabledYear =>
                    data.years.indexOf(disabledYear)
                );
                const availableYears = data.years.filter(
                    year => !disabledYears.includes(year)
                );

                // X-Axis Labels (Years)
                const labels = availableYears.map(year => {
                    groups.push({
                        label: String(year),
                        key: String(year),
                    });
                    return String(year);
                });

                // Create datasets for each indicator
                const datasets: BaseDataset[] = data.indicators.map(
                    (indicator: WegweiserIndicator, idx: number) => {
                        const color = helper.getChartBackgroundColor(
                            type,
                            widget.colorset,
                            widget.invertColors,
                            idx
                        );

                        return {
                            label: indicator.name,
                            labelKey: String(indicator.id),
                            data:
                                indicator.regionYearValues[regionIndex]?.filter(
                                    (val, idx) => !disabledYearsIdx.includes(idx)
                                ) ?? [],
                            dataKeys: labels,
                            backgroundColor: color,
                            borderColor: color,
                            pointRadius: widget.dotSize || 4,
                            borderWidth: widget.borderWidth || 2,
                            tension: widget.lineTension || 0,
                        };
                    }
                );

                return { labels, datasets, groups };
            };

            /**
             * Transforms WegweiserData into a Chart.js data format for a bar chart,
             * where indicators are on the x-axis, and years are in the legend for a single region.
             *
             * @param indicatorId - The ID of the indicator to focus on.
             * @param type - The WidgetType
             * @param widget - The WidgetConfig
             * @returns A Chart.js compatible data object.
             */
            const getWegweiserIndicatorDataGroupedByRegions = (
                indicatorId: number,
                type: WidgetType,
                widget: WidgetConfig
            ): { labels: string[]; datasets: BaseDataset[]; groups: ChartGroup[] } | null => {
                const data = widget.wegweiserData;
                if (!data) return null;

                const indicatorIndex = data.indicators.findIndex(
                    indicator => indicator.id === indicatorId
                );
                if (indicatorIndex === -1) {
                    throw new Error(`Indicator with ID ${indicatorId} not found.`);
                }

                const indicator = data.indicators[indicatorIndex];
                const groups: ChartGroup[] = [];

                // X-Axis Labels (Indicators)
                const labels = data.regions.map(region => {
                    groups.push({
                        label: region.name,
                        key: String(region.id),
                    });
                    return region.name;
                });

                const datasets: BaseDataset[] = data.years.map((year, idx) => {
                    const color = helper.getChartBackgroundColor(
                        type,
                        widget.colorset,
                        widget.invertColors,
                        idx
                    );

                    return {
                        label: String(year),
                        labelKey: String(year),
                        data: indicator.regionYearValues.map(
                            regionValues => regionValues[idx] ?? null
                        ),
                        dataKeys: data.regions.map(region => String(region.id)),
                        backgroundColor: color,
                        borderColor: color,
                        pointRadius: widget.dotSize || 4,
                        borderWidth: widget.borderWidth || 2,
                        tension: widget.lineTension || 0,
                    };
                });

                return { labels, datasets, groups };
            };

            /**
             * Transforms WegweiserData into a Chart.js data format for a bar chart,
             * where years are on the x-axis, and regions are in the legend for a single indicator.
             *
             * @param indicatorId - The ID of the indicator to focus on.
             * @param type - The WidgetType
             * @param widget - The WidgetConfig
             * @returns A Chart.js compatible data object.
             */
            const getWegweiserIndicatorDataGroupedByYears = (
                indicatorId: number,
                type: WidgetType,
                widget: WidgetConfig
            ): { labels: string[]; datasets: BaseDataset[]; groups: ChartGroup[] } | null => {
                // Find the selected indicator
                const data = widget.wegweiserData;
                if (!data) return null;

                const indicatorIndex = data.indicators.findIndex(
                    indicator => indicator.id === indicatorId
                );
                if (indicatorIndex === -1) {
                    throw new Error(`Indicator with ID ${indicatorId} not found.`);
                }

                const indicator = data.indicators[indicatorIndex];
                const groups: ChartGroup[] = [];

                const labels = data.years.map(year => {
                    groups.push({
                        label: String(year),
                        key: String(year),
                    });

                    return String(year);
                });

                // Create datasets for each region
                const datasets: BaseDataset[] = data.regions.map((region, idx) => {
                    const color = helper.getChartBackgroundColor(
                        type,
                        widget.colorset,
                        widget.invertColors,
                        idx
                    );

                    return {
                        label: region.name,
                        labelKey: String(region.id),
                        data: indicator.regionYearValues[idx] ?? [],
                        dataKeys: labels,
                        backgroundColor: color,
                        borderColor: color,
                        pointRadius: widget.dotSize || 4,
                        borderWidth: widget.borderWidth || 2,
                        tension: widget.lineTension || 0,
                    };
                });

                return { labels, datasets, groups };
            };

            /**
             * Transforms WegweiserData into a Chart.js data format for a bar chart,
             * where regions are on the x-axis, and years are in the legend for a single indicator.
             *
             * @param regionId - The ID of the indicator to focus on.
             * @param type - The WidgetType
             * @param widget - The WidgetConfig
             * @returns A Chart.js compatible data object.
             */
            const getWegweiserRegionDataGroupedByIndicators = (
                regionId: number,
                type: WidgetType,
                widget: WidgetConfig
            ): { labels: string[]; datasets: BaseDataset[]; groups: ChartGroup[] } | null => {
                const data = widget.wegweiserData;
                if (!data) return null;

                // Find the selected indicator
                const regionIndex = data.regions.findIndex(
                    region => region.id === regionId
                );
                if (regionIndex === -1) {
                    throw new Error(`Region with ID ${regionId} not found.`);
                }

                const groups: ChartGroup[] = [];
                // X-Axis Labels (Regions)
                const labels = data.indicators.map(indicator => {
                    groups.push({
                        label: indicator.name,
                        key: String(indicator.id),
                    });
                    return indicator.name;
                });

                const datasets: BaseDataset[] = data.years.map((year, idx) => {
                    const color = helper.getChartBackgroundColor(
                        type,
                        widget.colorset,
                        widget.invertColors,
                        idx
                    );

                    return {
                        label: String(year),
                        labelKey: String(year),
                        data: data.indicators.map(
                            indicator =>
                                indicator.regionYearValues[regionIndex][idx] ?? null
                        ),
                        dataKeys: data.indicators.map(indicator => String(indicator.id)),
                        backgroundColor: color,
                        borderColor: color,
                        pointRadius: widget.dotSize || 4,
                        borderWidth: widget.borderWidth || 2,
                        tension: widget.lineTension || 0,
                    };
                });

                return { labels, datasets, groups };
            };

            return {
                getGroupedWegweiserData,
                getWegweiserDataLayerDataGroupedByIndicators,
                getWegweiserDataLayerDataGroupedByRegions,
                getWegweiserRegionDataGroupedByYears,
                getWegweiserIndicatorDataGroupedByRegions,
                getWegweiserIndicatorDataGroupedByYears,
                getWegweiserRegionDataGroupedByIndicators,
            };
        }
        )
    );
}
